import RepositoryFactory from '@/repository/RepositoryFactory';
import { ContactToScheduleApplyPreconditionRequest } from '@/model/repository/manager/parentContacts/ContactToScheduleApplyPreconditionRepository';

// 設定画面が保存しようとしている変更。施設種別か利用申請のどちらか一方だけを持つ。
// 画面の値（利用申請の '0' / '1'、セレクトの文字列の施設種別）からの変換は呼び出し元が行う。
export type ContactToScheduleApplySettingChange = { serviceKind: number } | { reserveScheduleUse: boolean };

// 呼び出し元は素の JS でもあるため、型では防げない値を実行時に確かめる。
type UncheckedChange = Partial<Record<'serviceKind' | 'reserveScheduleUse', unknown>>;

const isPresent = (value: unknown) => value !== undefined && value !== null;

// 施設種別と利用申請のどちらか一方だけに値があり、その値の型が正しいことを確かめる。違えば response を持たない Error を投げる。
const validate = ({ serviceKind, reserveScheduleUse }: UncheckedChange): void => {
  const hasServiceKind = isPresent(serviceKind);
  const hasReserveScheduleUse = isPresent(reserveScheduleUse);
  if (hasServiceKind === hasReserveScheduleUse) {
    throw new Error('施設種別と利用申請は、どちらか一方だけを指定してください');
  }
  if (hasServiceKind && !Number.isInteger(serviceKind)) {
    throw new Error('施設種別は整数で指定してください');
  }
  if (hasReserveScheduleUse && typeof reserveScheduleUse !== 'boolean') {
    throw new Error('利用申請は true / false で指定してください');
  }
};

// validate を通った変更内容を、判定APIのクエリに変換する。
const toQuery = (change: ContactToScheduleApplySettingChange): ContactToScheduleApplyPreconditionRequest => {
  const { serviceKind, reserveScheduleUse } = change as UncheckedChange;
  return isPresent(serviceKind)
    ? { service_kind: serviceKind as number }
    : { reserve_schedule_use: reserveScheduleUse as boolean };
};

// 連絡即予定反映の前提条件を満たすかを判定APIに問い合わせ、eligible で resolve する。
// 失敗（HTTP エラー・通信エラー・入力の不正）は、前提条件を満たさない（false）と区別できるよう reject する。
// HTTP エラーと通信エラーは axios のエラーのまま、入力の不正は response を持たない Error で reject する。
export const getContactToScheduleApplyEligibility = async (
  change: ContactToScheduleApplySettingChange
): Promise<boolean> => {
  validate(change);
  const query = toQuery(change);
  const response = await RepositoryFactory.manager.contactToScheduleApplyPrecondition.get(query);
  return response.data.data.eligible;
};
